// src/components/NumberLineCanvas.tsx
/**
 * T-37: Integrated Canvas Composition
 * 
 * Main canvas component that layers and coordinates all visual elements:
 * - Layer 1: SVG Number Line (z-index: 10)
 * - Layer 2: Jump Indicators (z-index: 20) - integrated in NumberLine
 * - Layer 3: Constants Overlay (z-index: 30) - integrated in NumberLine  
 * - Layer 4: Annotation Canvas (z-index: 40)
 * - Layer 5: UI Overlays (z-index: 50)
 * 
 * Handles proper pointer event routing based on active tool mode
 * and maintains viewport synchronization across all layers.
 */

import React, { useRef, useEffect, useCallback, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../types/redux';
import { NumberLine } from './NumberLine';
import AnnotationCanvas from './AnnotationCanvas';
import { EquivalentFractionsView } from './EquivalentFractionsView';
import { CoordinateGridView } from './coordinateGrid';
import { selectActiveTool } from '../store/slices/toolModeSlice';

interface NumberLineCanvasProps {
  width: number;
  height: number;
  className?: string;
  exportRef?: React.RefObject<HTMLDivElement>;
  /**
   * Visible viewport width/height for layout-aligned elements (e.g., equivalent
   * fractions controls) when the canvas may be larger than the clipped view.
   */
  viewWidth?: number;
  viewHeight?: number;
  /**
   * Whether the canvas is being shown in fullscreen.
   * Used to reserve layout for top controls in equivalent fractions mode.
   */
  isFullscreen?: boolean;
}

interface LayerVisibility {
  numberLine: boolean;
  annotations: boolean;
  overlays: boolean;
}

export const NumberLineCanvas: React.FC<NumberLineCanvasProps> = ({
  width,
  height,
  className = '',
  exportRef,
  viewWidth,
  viewHeight,
  isFullscreen = false
}) => {
  const dispatch = useDispatch();
  const containerRef = useRef<HTMLDivElement>(null);
  const mergedContainerRef = useCallback(
    (el: HTMLDivElement | null) => {
      containerRef.current = el;
      if (exportRef && 'current' in exportRef) {
        (exportRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
      }
    },
    [exportRef]
  );
  const annotationCanvasRef = useRef<HTMLCanvasElement>(null);

  // Get state from Redux
  const activeTool = useSelector(selectActiveTool);
  const viewport = useSelector((state: RootState) => state.viewport);
  const preferences = useSelector((state: RootState) => state.preferences);
  const ui = useSelector((state: RootState) => state.ui);
  const annotations = useSelector((state: RootState) => state.annotations);
  const equivalentFractions = useSelector((state: RootState) => state.equivalentFractions);
  const layoutWidth = viewWidth ?? width;
  const layoutHeight = viewHeight ?? height;
  
  // Dark mode support
  const isDark = preferences.theme === 'dark';
  
  // Determine layer visibility based on tool mode and content
  const layerVisibility: LayerVisibility = useMemo(() => {
    const hasAnnotations = annotations.shapes.length > 0 || 
                          annotations.texts.length > 0 || 
                          annotations.strokes.length > 0;
    
    return {
      numberLine: true, // Always visible
      annotations: activeTool === 'annotation' || hasAnnotations,
      overlays: true, // For measurements, tooltips, etc.
    };
  }, [activeTool, annotations]);

  // Determine if annotations should be active
  const annotationsActive = activeTool === 'annotation';
  const useCoordinateGridMode = equivalentFractions.mode === 'coordinateGrid';
  const useEquivalentMode = equivalentFractions.mode === 'equivalentFractions';

  // Track scroll position of EquivalentFractionsView container
  const [scrollOffset, setScrollOffset] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (!useEquivalentMode) {
      setScrollOffset({ x: 0, y: 0 });
      return;
    }

    const container = document.querySelector('[data-equivalent-fractions-container]') as HTMLElement;
    if (!container) {
      setScrollOffset({ x: 0, y: 0 });
      return;
    }

    // Read initial scroll position
    const updateScrollOffset = () => {
      setScrollOffset({ 
        x: container.scrollLeft || 0, 
        y: container.scrollTop || 0 
      });
    };

    // Set initial value
    updateScrollOffset();

    // Use requestAnimationFrame to throttle scroll updates for better performance
    let rafId: number | null = null;
    const handleScroll = () => {
      if (rafId === null) {
        rafId = requestAnimationFrame(() => {
          updateScrollOffset();
          rafId = null;
        });
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [useEquivalentMode]);

  // Handle tool-specific cursor styling
  const getCursorClass = useCallback(() => {
    switch (activeTool) {
      case 'pan':
        return 'cursor-grab active:cursor-grabbing';
      case 'jump':
      case 'measurement':
        return 'cursor-crosshair';
      case 'annotation':
        return 'cursor-text';
      default:
        return 'cursor-default';
    }
  }, [activeTool]);

  // Viewport synchronization for annotation canvas
  const annotationViewportProps = useMemo(() => ({
    viewportOffset: { x: viewport.offsetX, y: viewport.offsetY },
    viewportScale: viewport.scale,
    orientation: viewport.orientation,
    // Respect user toggle; default true if undefined
    panWithNumberLine: ui.annotationLayer?.panWithNumberLine !== false,
    // Pass scroll offset when in equivalent fractions mode
    containerScrollOffset: useEquivalentMode ? scrollOffset : undefined,
  }), [viewport.offsetX, viewport.offsetY, viewport.scale, viewport.orientation, ui.annotationLayer?.panWithNumberLine, useEquivalentMode, scrollOffset]);

  // Handle container resize and maintain aspect ratio
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleResize = () => {
      // Update dimensions if needed - for now, use passed props
      // Future enhancement: auto-resize based on container
    };

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, []);

  // Debug logging for development
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('NumberLineCanvas: Tool changed to', activeTool);
      console.log('NumberLineCanvas: Annotations active', annotationsActive);
    }
  }, [activeTool, annotationsActive]);

  return (
          <div 
        ref={mergedContainerRef}
        className={`relative ${getCursorClass()} ${className}`}
        style={{ 
          width, 
          height,
          position: 'relative',
          overflow: 'hidden',
          userSelect: 'none',
        }}
        data-tool-mode={activeTool}
        data-numberline-container
        data-numberline-export-root
      >
        {/* Layer 0: Background (z-index: 0) */}
        <div 
          className={`absolute inset-0 transition-colors duration-200 ${
            isDark ? 'bg-gray-900' : 'bg-white'
          }`}
          style={{ zIndex: 0 }}
        />
        
        {/* Layer 1: SVG Number Line (z-index: 10) */}
        {/* Includes jumps (z-20) and constants (z-30) internally */}
        <div 
          className="absolute inset-0"
          style={{ 
            zIndex: 10,
            // Only block events when annotation tool is active
            pointerEvents: activeTool === 'annotation' ? 'none' : 'auto',
          }}
        >
          {useCoordinateGridMode ? (
            <CoordinateGridView
              width={width}
              height={height}
              className="w-full h-full"
              exportRef={exportRef}
            />
          ) : useEquivalentMode ? (
            <EquivalentFractionsView
              width={width}
              height={height}
              className="w-full h-full"
              layoutWidth={layoutWidth}
              layoutHeight={layoutHeight}
              isFullscreen={isFullscreen}
              exportRef={exportRef}
            />
          ) : (
            <NumberLine 
              width={width}
              height={height}
              className="w-full h-full"
              id="integrated-number-line"
              exportRef={exportRef}
              viewWidth={viewWidth}
              viewHeight={viewHeight}
            />
          )}
        </div>

      {/* Layer 4: Annotation Canvas (z-index: 40) */}
      {(
        <div 
          className="absolute inset-0"
          style={{ 
            zIndex: 40,
            // Only allow events when annotation tool is active
            pointerEvents: activeTool === 'annotation' ? 'auto' : 'none',
          }}
        >
          <AnnotationCanvas
            width={width}
            height={height}
            enabled={activeTool === 'annotation'}
            className="w-full h-full"
            zIndex={40}
            {...annotationViewportProps}
            lineBounds={{
              start: 0,
              end: width
            }}
          />
        </div>
      )}

      {/* Layer 5: UI Overlays (z-index: 50) */}
      {layerVisibility.overlays && (
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{ zIndex: 50 }}
        >
          {/* Measurement displays, tooltips, debug info */}
        </div>
      )}


    </div>
  );
};

// Hook for external components to interact with the canvas
export const useNumberLineCanvas = () => {
  const activeTool = useSelector(selectActiveTool);
  const viewport = useSelector((state: RootState) => state.viewport);
  
  const getCanvasState = useCallback(() => ({
    activeTool,
    viewport,
    layerCount: 5,
  }), [activeTool, viewport]);

  const isToolActive = useCallback((tool: string) => {
    return activeTool === tool;
  }, [activeTool]);

  return {
    getCanvasState,
    isToolActive,
    activeTool,
  };
};

export default NumberLineCanvas;